const DCP_MODEL_STORAGE_KEY = 'seacase_selected_phone_model';
const DCP_LEGACY_MODEL_STORAGE_KEY = 'seacase_selected_model';
const DCP_MODEL_CHANGE_EVENT = 'seacase:phone-model-change';
const DCP_LEGACY_MODEL_CHANGE_EVENT = 'seacase:model-changed';
const DCP_COLLECTION_FILTER_PARAM = 'filter.p.m.custom.design_collections';
const DCP_MODEL_FILTER_PARAM = 'filter.p.m.custom.phone_model';
const DCP_TEMPLATE_VIEW = 'dcp-products';
const dcpFilteredProductsCache = new Map();

function dcpNormalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function dcpNormalizeOption(value) {
  return dcpNormalizeText(value).replace(/[\s\-_\/()]+/g, '');
}

function dcpNormalizeMetaobjectId(value) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  if (normalized.startsWith('gid://shopify/Metaobject/')) return normalized;
  return `gid://shopify/Metaobject/${normalized}`;
}

function dcpParseStoredModel(storage, key) {
  try {
    const parsed = JSON.parse(storage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      handle: String(parsed.handle || '').trim(),
      name: String(parsed.name || '').trim(),
    };
  } catch (error) {
    return null;
  }
}

function dcpReadJson(root, selector) {
  const element = root.querySelector(selector);
  if (!element) return null;

  try {
    return JSON.parse(element.textContent || 'null');
  } catch (error) {
    return null;
  }
}

function dcpParseMetaobjectTokens(value) {
  return new Set(
    String(value || '')
      .split('||')
      .map(dcpNormalizeMetaobjectId)
      .filter(Boolean)
  );
}

function dcpCardMatches(card, collectionIds, modelId) {
  const productCollectionIds = dcpParseMetaobjectTokens(card.dataset.productCollectionIds);
  const productModelIds = dcpParseMetaobjectTokens(card.dataset.productModelIds);
  const collectionMatches = collectionIds.some((collectionId) => productCollectionIds.has(collectionId));
  return collectionMatches && productModelIds.has(modelId);
}

function dcpVariantMatches(variant, wanted) {
  const normalizedWanted = dcpNormalizeOption(wanted);
  if (!normalizedWanted) return true;

  return (variant?.options || []).some((option) => dcpNormalizeOption(option) === normalizedWanted);
}

function dcpFindBestVariant(variants, predicate) {
  const matches = variants.filter(predicate);
  return matches.find((variant) => variant.available) || matches[0] || null;
}

function dcpFindCardVariant(variants, activeModel, activeType) {
  const cardVariants = Array.isArray(variants) ? variants : [];
  if (!cardVariants.length) return null;

  const exactMatch = dcpFindBestVariant(
    cardVariants,
    (variant) =>
      (!activeModel || dcpVariantMatches(variant, activeModel)) &&
      (!activeType || dcpVariantMatches(variant, activeType))
  );
  if (exactMatch) return exactMatch;

  // The model can live only in custom.phone_model, but an active Type must
  // always match a variant option. Never fall back to another Type.
  if (activeType) {
    return dcpFindBestVariant(cardVariants, (variant) => dcpVariantMatches(variant, activeType));
  }

  if (activeModel) {
    const modelMatch = dcpFindBestVariant(cardVariants, (variant) =>
      dcpVariantMatches(variant, activeModel)
    );
    if (modelMatch) return modelMatch;
  }

  return cardVariants.find((variant) => variant.available) || cardVariants[0];
}

function dcpReadCardVariants(card) {
  return dcpReadJson(card, '[data-dcp-card-variants]') || [];
}

function dcpCardMatchesType(card, activeType) {
  if (!dcpNormalizeOption(activeType)) return true;
  return dcpReadCardVariants(card).some((variant) => dcpVariantMatches(variant, activeType));
}

function dcpUpdateCardGallery(card, variant) {
  const productCard = card.querySelector('product-card');
  if (!productCard) return;

  const applyVariant = () => {
    if (typeof productCard.filterGalleryByVariant !== 'function') return false;
    return productCard.filterGalleryByVariant(variant) === true;
  };

  if (!applyVariant() && window.customElements) {
    window.customElements.whenDefined('product-card').then(() => {
      requestAnimationFrame(() => {
        if (productCard.isConnected) applyVariant();
      });
    });
  }
}

function dcpApplyCardVariant(card, activeModel, activeType) {
  const variant = dcpFindCardVariant(dcpReadCardVariants(card), activeModel, activeType);
  if (!variant) return;

  // Some products store the model only in a metafield while PMG tags encode model and type.
  dcpUpdateCardGallery(card, {
    ...variant,
    options: [...(variant.options || []), activeModel, activeType].filter(Boolean),
  });

  const optionText = (variant.options || []).filter(Boolean).join(' · ');
  const optionBlock = card.querySelector('.text-block.h5');
  if (optionBlock && optionText) optionBlock.textContent = optionText;

  const price = card.querySelector('product-price [ref="priceContainer"] .price');
  if (price && variant.price) price.textContent = variant.price;

  const compareAtPrice = card.querySelector('product-price [ref="priceContainer"] .compare-at-price');
  if (compareAtPrice) {
    const compareGroup = compareAtPrice.closest('[role="group"]');
    if (variant.compare_at_price) {
      compareAtPrice.textContent = variant.compare_at_price;
      compareGroup?.removeAttribute('hidden');
    } else {
      compareGroup?.setAttribute('hidden', '');
    }
  }

  const links = card.querySelectorAll(
    'a[ref="productCardLink"], .product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]'
  );
  for (const link of links) {
    if (variant.url) link.setAttribute('href', variant.url);
  }

  const quickAddInput = card.querySelector('.quick-add input[name="id"]');
  if (quickAddInput && variant.id) {
    quickAddInput.value = variant.id;
    quickAddInput.disabled = !variant.available;
  }
}

function dcpBuildProductsUrl(productsUrl, collectionIds, modelId) {
  const url = new URL(productsUrl || '/collections/all', window.location.origin);
  url.searchParams.delete(DCP_COLLECTION_FILTER_PARAM);
  url.searchParams.delete(DCP_MODEL_FILTER_PARAM);
  url.searchParams.set('view', DCP_TEMPLATE_VIEW);
  url.searchParams.delete('section_id');

  for (const collectionId of collectionIds) {
    url.searchParams.append(DCP_COLLECTION_FILTER_PARAM, collectionId);
  }
  url.searchParams.set(DCP_MODEL_FILTER_PARAM, modelId);
  return url;
}

async function dcpRequestFilteredCards(url, collectionIds, modelId) {
  const response = await fetch(url.toString(), {
    headers: { Accept: 'text/html' },
    credentials: 'same-origin',
  });
  if (!response.ok) {
    throw new Error(`Filtered product request failed: ${response.status}`);
  }

  const html = await response.text();
  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const result = documentFragment.querySelector('[data-dcp-products-response]');
  if (!result) {
    throw new Error('Filtered product response was not found');
  }

  if (
    result.dataset.modelFilterActive !== 'true' ||
    result.dataset.collectionFilterActive !== 'true'
  ) {
    console.warn(
      '[Design collection products] Storefront metafield filters are unavailable; using card data fallback.'
    );
  }

  const seenProductIds = new Set();
  return Array.from(result.querySelectorAll('.product-grid__item')).filter((card) => {
    const productId = String(card.dataset.productId || '').trim();
    if (!productId || seenProductIds.has(productId)) return false;
    if (!dcpCardMatches(card, collectionIds, modelId)) return false;
    seenProductIds.add(productId);
    return true;
  });
}

function dcpFetchFilteredCards(productsUrl, collectionIds, modelId) {
  const normalizedCollectionIds = [...new Set(collectionIds.map(dcpNormalizeMetaobjectId).filter(Boolean))].sort();
  const normalizedModelId = dcpNormalizeMetaobjectId(modelId);
  const cacheKey = [productsUrl, normalizedModelId, ...normalizedCollectionIds].join('|');

  if (!dcpFilteredProductsCache.has(cacheKey)) {
    const sectionUrl = dcpBuildProductsUrl(productsUrl, normalizedCollectionIds, normalizedModelId);
    const request = dcpRequestFilteredCards(
      sectionUrl,
      normalizedCollectionIds,
      normalizedModelId
    );

    dcpFilteredProductsCache.set(cacheKey, request);
    request.catch(() => dcpFilteredProductsCache.delete(cacheKey));
  }

  return dcpFilteredProductsCache.get(cacheKey).then((cards) =>
    cards.map((card) => card.cloneNode(true))
  );
}

if (!customElements.get('design-collection-products')) {
  class DesignCollectionProducts extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.generation = 0;
      this.grid = this.querySelector('.dcp__grid');
      this.empty = this.querySelector('[data-dcp-empty]');
      this.collectionIds = this.readCollectionIds();
      this.models = this.readModels();
      this.maxProducts = Math.min(Math.max(Number.parseInt(this.dataset.maxProducts, 10) || 12, 1), 48);
      this.defaultTypeName = String(this.dataset.defaultTypeName || '').trim();
      this.defaultModel = this.resolveModel({
        id: this.dataset.defaultModelId,
        handle: this.dataset.defaultModelHandle,
        name: this.dataset.defaultModelName,
      });

      this.handleModelChange = (event) => this.applyModel(event.detail);
      this.handleStorage = (event) => {
        if (event.key !== DCP_MODEL_STORAGE_KEY) return;
        this.applyModel(dcpParseStoredModel(localStorage, DCP_MODEL_STORAGE_KEY));
      };
      this.handlePageShow = (event) => {
        if (!event.persisted) return;
        requestAnimationFrame(() => this.restoreFromPageCache());
      };

      window.addEventListener(DCP_MODEL_CHANGE_EVENT, this.handleModelChange, {
        signal: this.controller.signal,
      });
      window.addEventListener('storage', this.handleStorage, {
        signal: this.controller.signal,
      });
      window.addEventListener('pageshow', this.handlePageShow, {
        signal: this.controller.signal,
      });
      document.addEventListener(DCP_LEGACY_MODEL_CHANGE_EVENT, this.handleModelChange, {
        signal: this.controller.signal,
      });

      const storedModel =
        dcpParseStoredModel(localStorage, DCP_MODEL_STORAGE_KEY) ||
        dcpParseStoredModel(sessionStorage, DCP_LEGACY_MODEL_STORAGE_KEY);
      this.applyModel(storedModel || this.defaultModel);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.generation += 1;
    }

    readCollectionIds() {
      const collectionIds = dcpReadJson(this, '[data-dcp-collections]');
      if (!Array.isArray(collectionIds)) return [];
      return [...new Set(collectionIds.map(dcpNormalizeMetaobjectId).filter(Boolean))];
    }

    readModels() {
      const models = dcpReadJson(this, '[data-dcp-models]');
      if (!Array.isArray(models)) return [];
      return models
        .map((model) => ({
          id: dcpNormalizeMetaobjectId(model?.id),
          handle: String(model?.handle || '').trim(),
          name: String(model?.name || '').trim(),
        }))
        .filter((model) => model.id && model.name);
    }

    resolveModel(model) {
      if (!model || typeof model !== 'object') return null;

      const id = dcpNormalizeMetaobjectId(model.id);
      const handle = String(model.handle || '').trim();
      const name = dcpNormalizeText(model.name);
      return (
        this.models.find((candidate) => id && candidate.id === id) ||
        this.models.find((candidate) => handle && candidate.handle === handle) ||
        this.models.find((candidate) => name && dcpNormalizeText(candidate.name) === name) ||
        null
      );
    }

    restoreFromPageCache() {
      const storedModel =
        dcpParseStoredModel(localStorage, DCP_MODEL_STORAGE_KEY) ||
        dcpParseStoredModel(sessionStorage, DCP_LEGACY_MODEL_STORAGE_KEY);
      const currentModel = {
        id: this.dataset.currentModelId,
        handle: this.dataset.currentModelHandle,
        name: this.dataset.currentModelName,
      };

      // Cached source cards are pristine clones, so replacing the grid also resets slideshow state.
      this.applyModel(storedModel || currentModel, { forceRender: true });
    }

    async applyModel(model, { forceRender = false } = {}) {
      const selectedModel = this.resolveModel(model) || this.defaultModel || this.models[0];
      if (!selectedModel || !this.grid || this.collectionIds.length === 0) {
        this.grid?.replaceChildren();
        this.removeAttribute('aria-busy');
        this.updateEmptyState();
        return;
      }

      const selectedModelKey = selectedModel.id;
      if (
        !forceRender &&
        this.dataset.currentModelId === selectedModelKey &&
        this.dataset.currentQueryLoaded === 'true'
      ) {
        return;
      }

      const generation = ++this.generation;
      this.dataset.currentModelId = selectedModelKey;
      this.dataset.currentModelHandle = selectedModel.handle;
      this.dataset.currentModelName = selectedModel.name;
      delete this.dataset.currentQueryLoaded;
      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      this.empty.hidden = true;

      try {
        const cards = await dcpFetchFilteredCards(
          this.dataset.productsUrl,
          this.collectionIds,
          selectedModel.id
        );
        if (generation !== this.generation) return;

        // The section setting wins; Liquid supplies the highest-weight Type
        // when the template has no explicit default_phone_type.
        const visibleCards = cards
          .filter((card) => dcpCardMatchesType(card, this.defaultTypeName))
          .slice(0, this.maxProducts);
        visibleCards.forEach((card, index) => {
          card.classList.add('dcp__item', `product-grid__item--${index}`);
          card.setAttribute('ref', 'cards[]');
        });
        this.grid.replaceChildren(...visibleCards);
        visibleCards.forEach((card) => {
          dcpApplyCardVariant(card, selectedModel.name, this.defaultTypeName);
        });
        this.dataset.currentQueryLoaded = 'true';
      } catch (error) {
        if (generation !== this.generation) return;
        this.grid.replaceChildren();
        console.error('[Design collection products]', error);
      } finally {
        if (generation === this.generation) {
          this.removeAttribute('aria-busy');
          this.classList.remove('is-loading');
          this.updateEmptyState();
        }
      }
    }

    updateEmptyState() {
      if (!this.empty) return;
      this.empty.hidden = Boolean(this.grid?.children.length);
    }
  }

  customElements.define('design-collection-products', DesignCollectionProducts);
}
